import React, { type FC, useState, type PropsWithChildren } from 'react';

import { isDefined } from '@lucid/utils';

import Box from '../box/box';
import Icon from '../icon/icon';
import type { Lucid } from '../../types/types';
import Heading from '../heading/heading';
import Link from '../link/link';

export interface DropdownListProps {
  dropdowns: Lucid.Directus.Generated.Learning_Center_Translations_Discovery_Type[];
  shortCode?: Lucid.I18n.LanguagesShortCode;
}

const DropdownList: FC<PropsWithChildren<DropdownListProps & React.HTMLAttributes<HTMLDivElement>>> = ({
  dropdowns,
  shortCode,
  ...rest
}) => {
  const [activeDropdownKey, setActiveDropdownKey] = useState('');
  const filteredDropdowns = dropdowns.map(({ discovery_type_id }) => {
    const linkTranslations = discovery_type_id?.translations?.[0];
    const listOfLinks = linkTranslations?.link_sections?.map(({ list_of_links_id }) => {
      if (list_of_links_id && list_of_links_id?.link_list?.length) {
        const { heading, id, link_list } = list_of_links_id;
        const filteredLinks = link_list?.map(l => l.links_id).filter(isDefined);

        return {
          heading,
          id,
          filteredLinks,
        };
      }
    });

    return {
      header: linkTranslations?.name,
      currentKey: `${linkTranslations?.name}-${linkTranslations?.id}`,
      id: linkTranslations?.id,
      listOfLinks: listOfLinks || [],
      primaryLink: linkTranslations?.primary_link || { final_url: '', default_link_text: '' },
    };
  });

  const midIndex = Math.ceil(filteredDropdowns.length / 2);
  const columns = [filteredDropdowns.slice(0, midIndex), filteredDropdowns.slice(midIndex)];

  const hasLongChars = shortCode === 'ru' || shortCode === 'de';

  return (
    <Box
      {...rest}
      css={theme => ({
        label: 'dropdownList',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: theme.space.s16,
        [theme.breakpoints.bp768.min]: {
          gridTemplateColumns: '1fr 1fr',
        },
      })}
    >
      {columns.map((column, colIndex) => (
        <div key={colIndex}>
          {column.map(({ listOfLinks, header, currentKey, primaryLink }) => {
            const isActive = currentKey === activeDropdownKey;

            const itemClickHandler = () => {
              setActiveDropdownKey(isActive ? '' : currentKey);
            };

            return (
              <Box
                key={currentKey}
                css={theme => ({
                  backgroundColor: theme.colors.white,
                  maxHeight: isActive ? '100%' : theme.space.s80,
                  maxWidth: '100%',
                  marginBottom: theme.space.s16,
                })}
              >
                <Box
                  css={theme => [
                    {
                      border: `1px solid ${theme.colors.orange[20]}`,
                      maxWidth: '100%',
                      width: '100%',
                      height: theme.space.s80,
                      minHeight: theme.space.s80,
                      padding: 0,
                      '&:focus': {
                        outline: isActive ? 'none' : 'auto',
                      },
                    },
                    isActive && {
                      position: 'relative',
                      borderBottom: 'none',
                    },
                  ]}
                  onClick={itemClickHandler}
                >
                  <Box
                    css={theme => [
                      {
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: `0 ${theme.space.s32}px`,
                        height: '100%',
                        [theme.breakpoints.bp500.max]: {
                          padding: hasLongChars ? `0 0 0 ${theme.space.s16}px` : `0 ${theme.space.s32}px`,
                        },
                      },
                    ]}
                  >
                    <Heading as="h3" css={{ marginBottom: 0, maxWidth: '480px' }} variant="interactiveM500">
                      {header}
                    </Heading>
                    <Link
                      asButton
                      aria-expanded={isActive}
                      aria-label={header}
                      css={theme => ({
                        fontSize: theme.fontSizes.fs20,
                        background: 'none',
                        outline: 'none',
                        padding: `0 ${theme.space.s4}px`,
                        color: theme.colors.coolGrey[100],
                        border: 0,
                      })}
                      onClick={itemClickHandler}
                    >
                      <Icon
                        css={theme => [
                          {
                            transition: 'transform 0.5s',
                            transitionTimingFunction: theme.beziers.slowStartEase,
                            transform: 'rotate(-90deg)',
                          },
                          isActive && {
                            transform: 'rotate(90deg)',
                          },
                        ]}
                        hoverColor="coolGrey.100"
                        type="CarotRight"
                      />
                    </Link>
                  </Box>
                </Box>
                <Box
                  css={theme => ({
                    display: isActive ? 'flex' : 'none',
                    flexDirection: 'column',
                    width: dropdowns.length <= 1 ? '100%' : 'initial',
                    padding: `0px ${theme.space.s32}px`,
                    position: 'relative',
                    justifyContent: 'space-between',
                    border: `1px solid ${theme.colors.orange[20]}`,
                    borderTop: 'none',
                    borderRadius: 0,
                  })}
                >
                  {listOfLinks.length >= 1 ? (
                    listOfLinks?.map(subgroup => {
                      const { heading, id, filteredLinks } = subgroup!;

                      return (
                        heading &&
                        id &&
                        filteredLinks.length >= 1 && (
                          <Box key={`subgroup-${heading}-${id}`} css={theme => ({ paddingBottom: theme.space.s32 })}>
                            <Heading as="h3" variant="labelS500">
                              {heading}
                            </Heading>
                            <ul
                              css={theme => ({
                                display: 'flex',
                                flexDirection: 'column',
                                listStyleType: 'none',
                                margin: theme.space.s0,
                                lineHeight: theme.lineHeights.lh1_75,
                                gap: theme.space.s16,
                              })}
                            >
                              {filteredLinks?.map(link => (
                                <li key={`${link.final_url}-${link.id}`}>
                                  <Link
                                    href={link.final_url}
                                    iconAnimation="navigational"
                                    underline="hover"
                                    variant="secondary"
                                  >
                                    {link.default_link_text}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )
                      );
                    })
                  ) : (
                    <Link
                      css={theme => ({ paddingBottom: theme.space.s32 })}
                      href={primaryLink.final_url}
                      iconAnimation="navigational"
                      underline="hover"
                      variant="secondary"
                    >
                      {primaryLink.default_link_text}
                    </Link>
                  )}
                </Box>
              </Box>
            );
          })}
        </div>
      ))}
    </Box>
  );
};

export default DropdownList;
